import { useMemo } from "react";
import { Version } from "../note-history.types";

interface VersionWithNumber extends Version {
  versionNumber: number;
  isCurrentVersion: boolean;
}

interface UseVersionsWithNumbersReturn {
  sortedVersionsWithNumbers: VersionWithNumber[];
  getVersionInfo: (uuid: string) => {
    versionNumber: number;
    isCurrentVersion: boolean;
    formattedDate: string;
    displayText: string;
  } | null;
}

export const useVersionsWithNumbers = (
  versions: Version[] = [],
  totalVersions: number = 0,
  isSearching: boolean = false
): UseVersionsWithNumbersReturn => {
  const sortedVersionsWithNumbers = useMemo(() => {
    // Sort versions by created_on timestamp, most recent first
    const sorted = [...versions].sort(
      (a, b) =>
        new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
    );

    // Add version numbers and current version flags
    // When searching, don't assign version numbers to avoid confusion
    return sorted.map((version, index) => ({
      ...version,
      versionNumber: isSearching ? 0 : totalVersions - index,
      isCurrentVersion: !isSearching && index === 0,
    }));
  }, [versions, isSearching, totalVersions]);

  const getVersionInfo = useMemo(() => {
    return (uuid: string) => {
      const versionWithNumber = sortedVersionsWithNumbers.find(
        (v) => v.uuid === uuid
      );
      if (!versionWithNumber) return null;

      const formattedDate = new Date(
        versionWithNumber.created_on
      ).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const displayText = isSearching
        ? formattedDate // Just show date when searching
        : versionWithNumber.isCurrentVersion
        ? `Current (${formattedDate})`
        : `Version ${versionWithNumber.versionNumber} (${formattedDate})`;

      return {
        versionNumber: versionWithNumber.versionNumber,
        isCurrentVersion: versionWithNumber.isCurrentVersion,
        formattedDate,
        displayText,
      };
    };
  }, [sortedVersionsWithNumbers, isSearching]);

  return {
    sortedVersionsWithNumbers,
    getVersionInfo,
  };
};
